/**
 * /reserva-starlink-mini/checkout — Reservation form + Stripe redirect.
 *
 * Flow: user fills the form → POST /api/starlink/reservations with the
 * origin_url → backend creates a Stripe Checkout Session and returns
 * {checkout_url} → we redirect to Stripe. The user comes back to
 * /reserva-starlink-mini/exito?session_id=... (paid) or ?cancelled=1.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png";

const PROVINCIAS = [
  "A Coruña","Álava","Albacete","Alicante","Almería","Asturias","Ávila","Badajoz","Baleares","Barcelona","Burgos","Cáceres","Cádiz","Cantabria","Castellón","Ceuta","Ciudad Real","Córdoba","Cuenca","Girona","Granada","Guadalajara","Gipuzkoa","Huelva","Huesca","Jaén","La Rioja","Las Palmas","León","Lleida","Lugo","Madrid","Málaga","Melilla","Murcia","Navarra","Ourense","Palencia","Pontevedra","Salamanca","Santa Cruz de Tenerife","Segovia","Sevilla","Soria","Tarragona","Teruel","Toledo","Valencia","Valladolid","Vizcaya","Zamora","Zaragoza",
];

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

const EMPTY = {
  nombre: "", apellidos: "", email: "", telefono: "",
  direccion_calle: "", direccion_puerta: "", codigo_postal: "",
  poblacion: "", provincia: "", nif: "",
  acepta_grupo: false, acepta_plazo: false, acepta_terminos: false,
};

export default function StarlinkCheckoutPage() {
  const { setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wasCancelled = params.get("cancelled") === "1";

  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setLanguage("es"); document.title = "Reserva Starlink Mini · Pago seguro | GTX"; }, [setLanguage]);

  useEffect(() => {
    let stop = false;
    axios.get(`${API}/starlink/config`).then((r) => { if (!stop) setConfig(r.data); }).catch(() => {});
    return () => { stop = true; };
  }, []);

  const importeReserva = config?.importe_reserva ?? 119.7;
  const importeSaldo = config?.importe_saldo ?? 279.3;

  const canSubmit = useMemo(() => {
    return (
      form.nombre.trim().length >= 2 &&
      form.apellidos.trim().length >= 1 &&
      /.+@.+\..+/.test(form.email) &&
      form.telefono.replace(/\D/g, "").length >= 6 &&
      form.direccion_calle.trim().length >= 3 &&
      form.codigo_postal.trim().length >= 3 &&
      form.poblacion.trim().length >= 2 &&
      form.provincia.trim().length >= 2 &&
      form.acepta_grupo && form.acepta_plazo && form.acepta_terminos &&
      !submitting
    );
  }, [form, submitting]);

  const handle = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form, origin_url: window.location.origin };
      const r = await axios.post(`${API}/starlink/reservations`, payload);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;  // → Stripe hosted
      } else {
        setError("No se pudo iniciar el pago. Inténtalo de nuevo.");
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al procesar la reserva.";
      setError(typeof msg === "string" ? msg : "Error al procesar la reserva.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise" data-testid="starlink-checkout-page">
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/8 bg-[#0A0A0F]/90 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/reserva-starlink-mini" className="flex items-center gap-3 text-white/70 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" />
            Volver a la campaña
          </Link>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <Lock className="w-3.5 h-3.5" />
            Pago seguro por Stripe
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* LEFT — Form */}
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="space-y-6"
          data-testid="checkout-form"
        >
          {wasCancelled && (
            <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-3 flex items-start gap-3 text-sm text-orange-200">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              Pago cancelado. Tus datos siguen aquí — puedes reintentarlo cuando quieras.
            </div>
          )}

          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-2">Paso final</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mb-2">Reserva tu Starlink Mini</h1>
            <p className="text-white/60 text-sm">Rellena tus datos, paga {fmtEUR(importeReserva)} de reserva y ya estás dentro del lote. El resto ({fmtEUR(importeSaldo)}) lo pagas cuando el kit llega a nuestro almacén.</p>
          </div>

          {/* Personal */}
          <fieldset className="space-y-3">
            <legend className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Datos personales</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nombre *" value={form.nombre} onChange={handle("nombre")} testid="f-nombre" />
              <Field label="Apellidos *" value={form.apellidos} onChange={handle("apellidos")} testid="f-apellidos" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Email *" type="email" value={form.email} onChange={handle("email")} testid="f-email" />
              <Field label="Teléfono *" value={form.telefono} onChange={handle("telefono")} placeholder="+34 600 000 000" testid="f-telefono" />
            </div>
            <Field label="NIF / CIF (opcional — para factura)" value={form.nif} onChange={handle("nif")} testid="f-nif" />
          </fieldset>

          {/* Address */}
          <fieldset className="space-y-3">
            <legend className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Dirección de envío</legend>
            <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
              <Field label="Calle y número *" value={form.direccion_calle} onChange={handle("direccion_calle")} testid="f-calle" />
              <Field label="Puerta / Piso" value={form.direccion_puerta} onChange={handle("direccion_puerta")} testid="f-puerta" />
            </div>
            <div className="grid sm:grid-cols-[1fr_2fr] gap-3">
              <Field label="Código Postal *" value={form.codigo_postal} onChange={handle("codigo_postal")} testid="f-cp" />
              <Field label="Población *" value={form.poblacion} onChange={handle("poblacion")} testid="f-poblacion" />
            </div>
            <label className="block">
              <span className="block text-xs text-white/60 mb-1.5">Provincia *</span>
              <select
                value={form.provincia}
                onChange={handle("provincia")}
                data-testid="f-provincia"
                className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/40"
              >
                <option value="" className="bg-[#0F0F16]">Selecciona provincia…</option>
                {PROVINCIAS.map((p) => <option key={p} value={p} className="bg-[#0F0F16]">{p}</option>)}
              </select>
            </label>
          </fieldset>

          {/* Consents */}
          <fieldset className="space-y-2.5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <legend className="text-xs font-bold uppercase tracking-widest text-white/50 px-2">Condiciones (obligatorias)</legend>
            <Check2 checked={form.acepta_grupo} onChange={handle("acepta_grupo")} testid="c-grupo">
              Entiendo que se trata de una <b>compra colectiva</b>: el pedido se cursa cuando reunimos 100 reservas y la entrega se realiza en 4-6 semanas desde el cierre del lote (máximo 8 semanas).
            </Check2>
            <Check2 checked={form.acepta_plazo} onChange={handle("acepta_plazo")} testid="c-plazo">
              Acepto pagar hoy <b>{fmtEUR(importeReserva)}</b> como reserva y el saldo restante de <b>{fmtEUR(importeSaldo)}</b> antes del envío, cuando la mercancía llegue al almacén.
            </Check2>
            <Check2 checked={form.acepta_terminos} onChange={handle("acepta_terminos")} testid="c-terminos">
              He leído y acepto los <Link to="/legal" className="underline text-blue-300">términos y condiciones</Link> y la <Link to="/privacy" className="underline text-blue-300">política de privacidad</Link>. Puedo cancelar y recuperar el 100 % en cualquier momento antes del envío.
            </Check2>
          </fieldset>

          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="checkout-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="pay-btn"
            className="w-full bg-blue-500 hover:bg-blue-400 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed text-white rounded-xl px-6 py-5 text-lg font-black tracking-wide transition-all"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirigiendo a Stripe…</span>
            ) : (
              <>PAGAR {fmtEUR(importeReserva)} Y RESERVAR MI PLAZA</>
            )}
          </button>
          <p className="text-center text-xs text-white/40">Serás redirigido a Stripe para completar el pago con tarjeta.</p>
        </motion.form>

        {/* RIGHT — Order summary */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-blue-500/30 bg-[#0F0F16] overflow-hidden">
            <div className="p-5 border-b border-white/10 flex items-center gap-3">
              <img src={LOGO_URL} alt="GTX" className="w-10 h-10 rounded-md" />
              <div>
                <div className="text-sm font-bold">Global Tech Xperts</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Starlink Mini · lote #1</div>
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Row k="Producto" v="Starlink Mini Kit" />
              <Row k="Precio total" v={fmtEUR(config?.precio_total ?? 399)} />
              <Row k="Envío" v="Gratis España" />
              <Row k="Entrega estimada" v={config?.plazo_entrega || "4 a 6 semanas"} />
              <div className="border-t border-white/10 my-3" />
              <Row k="Pagas hoy (30%)" v={fmtEUR(importeReserva)} highlight />
              <Row k="Saldo antes del envío" v={fmtEUR(importeSaldo)} />
            </div>
            <div className="p-5 bg-blue-500/10 border-t border-blue-500/25">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" />
                <div className="text-xs text-white/70 leading-relaxed">
                  <b className="text-white">Devolución 100 %.</b> Si no reunimos las 100 reservas o si te lo piensas mejor antes del envío, te devolvemos íntegramente y sin preguntas.
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/mi-reserva")}
            className="mt-4 w-full text-xs text-white/50 hover:text-white/80 underline"
          >
            ¿Ya reservaste? Consulta el estado de tu pedido
          </button>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs text-white/60 mb-1.5">{label}</span>
      <input
        {...props}
        data-testid={testid}
        className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/40"
      />
    </label>
  );
}

function Check2({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-3 text-sm text-white/80 leading-relaxed cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testid}
        className="mt-1 w-4 h-4 rounded border-white/30 bg-white/5 accent-blue-500 shrink-0"
      />
      <span>{children}</span>
    </label>
  );
}

function Row({ k, v, highlight = false }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/50">{k}</span>
      <span className={highlight ? "text-blue-300 font-black text-lg" : "font-semibold"}>{v}</span>
    </div>
  );
}
